/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import { modifier } from 'ember-modifier';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  groupBySection,
  INSTRUCTIONS_MAX_LENGTH,
  LABEL_MAX_LENGTH,
  MAX_ITEMS,
  NAME_MAX_LENGTH,
  SECTION_MAX_LENGTH,
  type ChecklistSection,
  type ChecklistTemplateItem,
} from 'uplisting-frontend/models/checklist-template';

export interface DraftItem extends ChecklistTemplateItem {
  uid: string;
}

interface IArgs {
  template: ModelRegistry['checklist-template'];
  property: ModelRegistry['property'] | undefined;
  onClose(): void;
}

interface ChecklistTemplatePanelSignature {
  // The panel's root is an <aside>, as the task detail and assignment rule
  // panels are.
  Element: HTMLElement;

  Args: IArgs;
}

let nextUid = 0;

function uid(): string {
  nextUid += 1;

  return `draft-${nextUid}`;
}

function toDraft(item: ChecklistTemplateItem): DraftItem {
  return {
    uid: uid(),
    section: item.section ?? null,
    label: item.label,
    instructions: item.instructions ?? null,
    position: item.position ?? 0,
    required: item.required ?? false,
    requires_photo: item.requires_photo ?? false,
  };
}

function toItem(draft: DraftItem): ChecklistTemplateItem {
  const { section, label, instructions, position, required, requires_photo } =
    draft;

  return {
    section,
    label,
    instructions,
    position,
    required,
    requires_photo,
  };
}

function fingerprint(name: string, items: ChecklistTemplateItem[]): string {
  return JSON.stringify([
    name.trim(),
    items.map(toDraft).map((draft) => ({
      ...toItem(draft),
      position: 0,
    })),
  ]);
}

export default class UiChecklistTemplatePanelComponent extends Component<ChecklistTemplatePanelSignature> {
  @service intl!: Services['intl'];
  @service sentry!: Services['sentry'];
  @service notifications!: Services['notifications'];

  @service('repositories/checklist-template')
  checklistTemplateRepository!: Services['repositories/checklist-template'];

  @tracked name = '';
  @tracked items: DraftItem[] = [];
  @tracked newLabel = '';
  @tracked newSection = '';
  @tracked isSaving = false;
  @tracked showDeleteModal = false;
  @tracked showDiscardModal = false;

  constructor(owner: Owner, args: IArgs) {
    super(owner, args);

    this.#resetDraft();
  }

  @cached
  get template(): ModelRegistry['checklist-template'] {
    return this.args.template;
  }

  get titleId(): string {
    return `${guidFor(this)}-title`;
  }

  get nameId(): string {
    return `${guidFor(this)}-name`;
  }

  get sectionsListId(): string {
    return `${guidFor(this)}-sections`;
  }

  @cached
  get sections(): ChecklistSection<DraftItem>[] {
    return groupBySection(this.items);
  }

  @cached
  get sectionNames(): string[] {
    return this.sections
      .map((group) => group.section)
      .filter((section): section is string => !!section);
  }

  @cached
  get savedFingerprint(): string {
    const { template } = this;

    return fingerprint(template.name ?? '', template.items ?? []);
  }

  @cached
  get isDirty(): boolean {
    return (
      fingerprint(this.name, this.items.map(toItem)) !== this.savedFingerprint
    );
  }

  @cached
  get isBusy(): boolean {
    return this.isSaving;
  }

  get nameMaxLength(): number {
    return NAME_MAX_LENGTH;
  }

  get labelMaxLength(): number {
    return LABEL_MAX_LENGTH;
  }

  get sectionMaxLength(): number {
    return SECTION_MAX_LENGTH;
  }

  get instructionsMaxLength(): number {
    return INSTRUCTIONS_MAX_LENGTH;
  }

  get maxItems(): number {
    return MAX_ITEMS;
  }

  @cached
  get invalidUids(): string[] {
    return this.items
      .filter(
        (item) =>
          !item.label.trim() ||
          item.label.length > LABEL_MAX_LENGTH ||
          (item.section ?? '').length > SECTION_MAX_LENGTH ||
          (item.instructions ?? '').length > INSTRUCTIONS_MAX_LENGTH,
      )
      .map((item) => item.uid);
  }

  @cached
  get isNameTooLong(): boolean {
    return this.name.length > NAME_MAX_LENGTH;
  }

  @cached
  get isFull(): boolean {
    return this.items.length >= MAX_ITEMS;
  }

  @cached
  get canSave(): boolean {
    return (
      this.isDirty &&
      !this.isBusy &&
      !this.isNameTooLong &&
      this.invalidUids.length === 0
    );
  }

  @cached
  get blockedReason(): string | undefined {
    if (!this.isDirty || this.canSave) {
      return undefined;
    }

    if (this.isNameTooLong) {
      return this.intl.t('action.changeover_settings.errors.name_too_long', {
        count: NAME_MAX_LENGTH,
      });
    }

    return this.intl.t('action.changeover_settings.errors.items_invalid');
  }

  @action
  isInvalid(item: DraftItem): boolean {
    return this.invalidUids.includes(item.uid);
  }

  @action
  isFirstInSection(item: DraftItem): boolean {
    return this.#siblingsOf(item).indexOf(item) === 0;
  }

  @action
  isLastInSection(item: DraftItem): boolean {
    const siblings = this.#siblingsOf(item);

    return siblings.indexOf(item) === siblings.length - 1;
  }

  @action
  handleTemplateChanged(): void {
    this.#resetDraft();
  }

  @action
  handleNameInput(event: Event): void {
    this.name = (event.target as HTMLInputElement).value;
  }

  @action
  handleNewLabelInput(event: Event): void {
    this.newLabel = (event.target as HTMLInputElement).value;
  }

  @action
  handleNewSectionInput(event: Event): void {
    this.newSection = (event.target as HTMLInputElement).value;
  }

  @action
  handleNewItemKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();

      this.handleAddItem();
    }
  }

  @action
  handleAddItem(): void {
    const label = this.newLabel.trim();

    if (!label || this.isFull) {
      return;
    }

    const section = this.newSection.trim() || null;

    this.items = [
      ...this.items,
      {
        uid: uid(),
        section,
        label,
        instructions: null,
        position: this.items.length,
        required: false,
        requires_photo: false,
      },
    ];
    this.newLabel = '';
    this.newSection = '';
  }

  @action
  handleLabelInput(item: DraftItem, event: Event): void {
    this.#patch(item, { label: (event.target as HTMLInputElement).value });
  }

  @action
  handleInstructionsInput(item: DraftItem, event: Event): void {
    this.#patch(item, {
      instructions: (event.target as HTMLTextAreaElement).value,
    });
  }

  @action
  handleToggleRequired(item: DraftItem): void {
    this.#patch(item, { required: !item.required });
  }

  @action
  handleToggleRequiresPhoto(item: DraftItem): void {
    this.#patch(item, { requires_photo: !item.requires_photo });
  }

  @action
  handleMove(item: DraftItem, direction: -1 | 1, event: Event): void {
    const siblings = this.#siblingsOf(item);
    const neighbour = siblings[siblings.indexOf(item) + direction];

    if (!neighbour) {
      return;
    }

    const items = [...this.items];
    const from = items.indexOf(item);
    const to = items.indexOf(neighbour);

    items[from] = neighbour;
    items[to] = item;

    this.items = items;

    this.#keepFocusOn(event.target as HTMLElement, item.uid);
  }

  @action
  handleRemoveItem(item: DraftItem, event: Event): void {
    const index = this.items.indexOf(item);

    this.items = this.items.filter((candidate) => candidate !== item);

    const next = this.items[index] ?? this.items[index - 1];

    this.#focusAfterRender(
      next
        ? `[data-test-checklist-template-item="${next.uid}"] [data-test-checklist-template-item-label]`
        : '[data-test-checklist-template-new-item-label]',
      event.target as HTMLElement,
    );
  }

  @action
  handleRequestClose(): void {
    if (this.isDirty) {
      this.showDiscardModal = true;
    } else {
      this.args.onClose();
    }
  }

  @action
  handleDiscard(): void {
    this.showDiscardModal = false;
    this.args.onClose();
  }

  handlePanelKeyboard = modifier(() => {
    const handleKeydown = (event: KeyboardEvent): void => {
      if (
        event.key === 'Escape' &&
        !this.showDiscardModal &&
        !this.showDeleteModal
      ) {
        this.handleRequestClose();
      }
    };

    window.document.addEventListener('keydown', handleKeydown);

    return () => window.document.removeEventListener('keydown', handleKeydown);
  });

  focusOnOpen = modifier((element: HTMLElement) => {
    element.querySelector<HTMLInputElement>('input')?.focus();
  });

  @action
  @waitFor
  async handleSave(): Promise<void> {
    if (!this.canSave) {
      return;
    }

    const { template } = this;
    const previous = { name: template.name, items: template.items };

    this.isSaving = true;

    try {
      template.name =
        this.name.trim() ||
        this.intl.t('action.changeover_settings.default_name');
      template.items = this.items.map(toItem).map((item) => ({
        ...item,
        instructions: item.instructions?.trim() || null,
      }));

      await this.checklistTemplateRepository.save(template);

      this.notifications.info();
      this.args.onClose();
    } catch (error) {
      template.name = previous.name;
      template.items = previous.items;

      this.sentry.captureException(error);
      this.#notifyError(error);
    } finally {
      this.isSaving = false;
    }
  }

  @action
  @waitFor
  async handleDelete(): Promise<void> {
    try {
      await this.checklistTemplateRepository.destroyRecord(this.template);

      this.notifications.info();
      this.args.onClose();
    } catch (error) {
      this.#notifyError(error);
    }
  }

  #notifyError(error: unknown): void {
    const first = (error as { errors?: unknown[] })?.errors?.[0];
    const message =
      typeof first === 'string'
        ? first
        : (first as { detail?: string })?.detail;

    if (message) {
      this.notifications.error(message, true);
    } else {
      this.notifications.error();
    }
  }

  #siblingsOf(item: DraftItem): DraftItem[] {
    return this.items.filter(
      (candidate) =>
        (candidate.section || undefined) === (item.section || undefined),
    );
  }

  #patch(item: DraftItem, changes: Partial<DraftItem>): void {
    this.items = this.items.map((candidate) =>
      candidate === item ? { ...candidate, ...changes } : candidate,
    );
  }

  #keepFocusOn(target: HTMLElement, uid: string): void {
    this.#focusAfterRender(
      `[data-test-checklist-template-item="${uid}"] [data-test-checklist-template-item-label]`,
      target,
    );
  }

  #focusAfterRender(selector: string, target: HTMLElement): void {
    const root = target.closest('[data-test-checklist-template]');

    if (!root) {
      return;
    }

    window.requestAnimationFrame(() =>
      root.querySelector<HTMLElement>(selector)?.focus(),
    );
  }

  #resetDraft(): void {
    const { template } = this;

    this.name = template.name ?? '';
    this.items = (template.items ?? []).map(toDraft);
    this.newLabel = '';
    this.newSection = '';
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::ChecklistTemplate::Panel': typeof UiChecklistTemplatePanelComponent;
  }
}
